import { c as _c } from "react/compiler-runtime";
import { ActionIcon, Button, Combobox as Combo, useCombobox, Tooltip, Alert, CheckIcon, Switch, Menu, Loader } from '@mantine/core';
import { usePortal } from 'components/portal/portal';
import { ProfileUI } from 'components/portal/profileTypes';
import { classes } from 'components/theme/theme';
import { StyleProps } from 'components';
import { SubqFetched, approvedStatuses, isApproved, subqStatusMd, useSubqQuery } from './subq';
import { subqMd } from 'shared/forecast/subq';
import { SubqStatus, SubqType, Forecast as F, Resolution, ResolutionKeyDependents, zSubqCfg, questionForecastMd, QuestionForecastType, subqTypes, questionTypeMd, Bounds, Subqs, SubqCfg, ShowForecastState, QuestionCfg, questionResultQuestionProps, QuestionResultsQuestion, ResolutionsStandalone, ResolutionForecasts } from 'shared/forecast';
import { delay, sansOk } from 'shared/promise';
import { NotNull, Prettify } from 'shared/type';
import { mapEntries, entries, fromEntries, filterObj, notNullOrEmpty } from 'shared/collection';
import { keys } from 'shared/collection';
import { ChildrenProps } from 'shared/ui/uiType';
import { doSafe } from 'shared/promise';
import { MasonryGrid, LabelType, SErrorBoundary, WrapIf, HrText } from 'shared/ui';
import { VoteButtons, VoteButtonsProps } from './VoteButtons';
import { useCommentExpand } from 'components/discussion/comments';
import { CommentStatuses } from 'components/discussion/CommentStatus';
import { ReactNode, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { groupBy, isEmpty, mapValues, omit, pick, pipe, sortBy, unique } from 'remeda';
import { HiClipboardCopy, HiExclamationCircle, HiRefresh, HiSelector, HiTrash, HiViewGrid, HiViewList } from 'react-icons/hi';
import { useStickyOrder } from 'components/hooks/useStickyOrder';
import { match, P } from 'ts-pattern';
import { DbFormSubset, UseDbForm, useDbForm } from 'components/hooks/useDbForm';
import { QuestionStatusButton } from './QuestionStatus';
import { CommentsStats, useCommentStats } from 'components/discussion/Comments';
import { useQueryString, useQueryStringValues } from 'utils/useQueryString';
import { QuestionContext, QuestionQuery, useQuestionContext, zQuestionQuery } from './questionComponentTypes';
import { OrderableList } from 'components/form/OrderableList';
import { IconContext } from 'react-icons';
import { upperFirst, useLocalStorage } from '@mantine/hooks';
import { QuestionTileItems } from './QuestionTile';
import { TaskedStatus } from './Participation';
import { ResolutionsEdit } from './Resolution';
import { showError } from 'components/notify';
import { IconAmount, IconCause, IconConsequence, IconLikelihood, IconRelated } from 'components/icons/SubqIcons';
import { OverflowMenu } from 'components/Menu';
import { openConfirmModal } from '@mantine/modals';
import { CardCollapsable } from 'components/Card';
import { O } from 'ts-toolbelt';
import { useMutation } from '@tanstack/react-query';
import { MdHome } from 'react-icons/md';
import { twMerge } from 'tailwind-merge';
import { TourInfo, useTourContext, tourTipProps } from 'components/portal/Tour';
import { NewSubqButton } from './SubqNew';
import { Resolution as R } from 'shared/forecast/resolution';
import { Sticky } from 'components/Sticky';
import { QuestionCommonForm, QuestionTitleInput, QuestionSubtitleInput } from './QuestionForm';
import { SubqForecasts } from './SubqForecast';
import { QuestionTitle } from './QuestionTitle';
import { EditingButton } from 'components/form/EditingButton';
import { DateTime } from 'luxon';
import { useQuestionForm, useQuestionQuery } from './useQuestions';
import { ForecastFetched, UseForecastGroupApp, useForecasts } from './useForecasts';
import { ReactionCfg, ReactionSummaryOfType, useQuestionVotesQuery, useVote } from './reactions';
import { dupeQuestion } from './dupe';
import { ChipRadioGroup } from 'components/form/ChipRadioGroup';

// #region Layout

const subqByType = <T extends {
  cfg: {
    subquestionType: SubqType;
  };
},>(subqs: T[]) => fromEntries(sortBy(entries(groupBy(subqs, s => s.cfg.subquestionType)), ([k, _]) => subqTypes.findIndex(t => t == k)));
const zSubqFiltersQuery = zQuestionQuery.pick({
  subquestion: true,
  subqDisplayMode: true,
  showForecasts: true,
  subq_resolution_closed: true,
  subq_resolution_open: true,
  showQuestionForecast: true,
  showDraft: true
});
export const useSubqFilters = () => {
  const $ = _c(14);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      pick: _temp
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    query,
    setQuery
  } = useQueryString(zSubqFiltersQuery, t0);
  const {
    profile
  } = usePortal();
  let t1;
  if ($[1] !== profile || $[2] !== query.showForecasts) {
    t1 = query.showForecasts ?? (profile.hasRole("forecaster") ? "all" : "none");
    $[1] = profile;
    $[2] = query.showForecasts;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const showForecastsState = t1;
  let t2;
  if ($[4] !== profile) {
    t2 = profile?.hasPermission("forecast");
    $[4] = profile;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const showForecastsDisabled = !t2;
  let t3;
  if ($[6] !== setQuery) {
    t3 = c => {
      setQuery({
        subq_resolution_closed: undefined,
        subq_resolution_open: undefined,
        showQuestionForecast: undefined,
        showForecasts: c as any
      });
    };
    $[6] = setQuery;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const setShowForecasts = t3;
  let t4;
  if ($[8] !== query || $[9] !== setQuery || $[10] !== setShowForecasts || $[11] !== showForecastsDisabled || $[12] !== showForecastsState) {
    t4 = {
      showForecasts: showForecastsState,
      showForecastsDisabled,
      setShowForecasts,
      query,
      setQuery
    };
    $[8] = query;
    $[9] = setQuery;
    $[10] = setShowForecasts;
    $[11] = showForecastsDisabled;
    $[12] = showForecastsState;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
};

/** just the data needed to structure the top level list. useSubqLayoutGroup and useSubqItem get the info they need from various hooks that utilize the cahe */
export const useSubqSection = ({
  question_id
}: {
  question_id: string;
}) => {
  const questionQuery = useQuestionQuery({
    question_id,
    pick: r => ({
      id: r.id,
      ...pick(r.cfg, ['subquestionOrder', 'title', 'disableProposed'])
    })
  });
  const question = questionQuery.data?.ok ? sansOk(questionQuery.data) : undefined;
  const {
    showForecasts,
    showForecastsDisabled,
    query,
    setQuery
  } = useSubqFilters();
  const {
    showDraft
  } = query;
  const uSubq = useSubqQuery({
    question_id,
    showDraft,
    groupsOnly: true,
    pick: ({
      id,
      created_at,
      cfg
    }) => ({
      id,
      created_at,
      cfg: pick(cfg, ['subquestionOrder', 'status', 'subquestionType', 'childrenEnabled'])
    })
  });
  const subqGroups = useMemo(() => {
    if (!uSubq.data || !question) return undefined;
    return Subqs.group(uSubq.data, {
      question,
      showForecasts: showForecasts
    }).map(({
      subqs,
      ...g
    }) => g); // omit the subq's because we filter them all out for the top level
  }, [uSubq.data, showForecasts, questionQuery.data]);
  return {
    subqGroups,
    showForecastsDisabled,
    question,
    showForecasts,
    query,
    setQuery
  };
};
export type SubqLayout = ReturnType<typeof useSubqSection>;
export type SubqSectionGroup = NotNull<SubqLayout['subqGroups']>[number];
type SubquestionsSectionOptions = Pick<QuestionCfg, 'disableProposed'>;
export const gridModeItems = [{
  value: 'grid',
  label: 'Grid',
  tip: 'Display subquestions in a grid'
}, {
  value: 'list',
  label: 'List',
  tip: 'Display subquestions in a list'
}] as const;
export type GridMode = (typeof gridModeItems)[number]['value'];
export const useGridMode = () => {
  const $ = _c(4);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      key: "question-grid-mode",
      defaultValue: "grid"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [gridMode, setGridMode] = useLocalStorage(t0);
  let t1;
  if ($[1] !== gridMode || $[2] !== setGridMode) {
    t1 = {
      gridMode,
      setGridMode
    };
    $[1] = gridMode;
    $[2] = setGridMode;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
};
type UseGridMode = ReturnType<typeof useGridMode>;
export const GridModeChips = t0 => {
  const $ = _c(7);
  const {
    gridMode,
    setGridMode
  } = t0;
  const {
    capture
  } = usePortal();
  let t1;
  if ($[0] !== capture || $[1] !== setGridMode) {
    t1 = v => {
      setGridMode(v);
      capture("view_mode_changed", {
        view_mode: v
      });
    };
    $[0] = capture;
    $[1] = setGridMode;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      classNames: {
        label: "!px-3 font-semibold"
      }
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== gridMode || $[5] !== t1) {
    t3 = <ChipRadioGroup value={gridMode} onChange={t1} items={gridModeItems} renderLabel={_temp7} chipProps={t2} />;
    $[4] = gridMode;
    $[5] = t1;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
export const SubquestionsSection = t0 => {
  const $ = _c(13);
  const {
    children,
    disableProposed,
    gridMode
  } = t0;
  const {
    question_id
  } = useQuestionContext();
  let t1;
  if ($[0] !== question_id) {
    t1 = {
      question_id
    };
    $[0] = question_id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    subqGroups,
    showForecastsDisabled,
    question,
    showForecasts,
    query,
    setQuery
  } = useSubqSection(t1);
  const {
    subqDisplayMode
  } = query;
  let t2;
  if ($[2] !== children || $[3] !== disableProposed || $[4] !== gridMode || $[5] !== query.subquestion || $[6] !== question || $[7] !== setQuery || $[8] !== showForecasts || $[9] !== showForecastsDisabled || $[10] !== subqDisplayMode || $[11] !== subqGroups) {
    t2 = question ? <div className="mb-[20em] space-y-4" onClick={() => {
      if (query?.subquestion) {
        setQuery({
          subquestion: undefined
        });
      }
    }}>{subqGroups?.map(group => <SubqGroup key={group.id} {...{
        disableProposed,
        showForecastsDisabled,
        question,
        showForecasts,
        group,
        subqDisplayMode,
        gridMode
      }} singleGroup={subqGroups.length == 1}>{group.isRootQuestion ? children : undefined}</SubqGroup>)}</div> : undefined;
    $[2] = children;
    $[3] = disableProposed;
    $[4] = gridMode;
    $[5] = query.subquestion;
    $[6] = question;
    $[7] = setQuery;
    $[8] = showForecasts;
    $[9] = showForecastsDisabled;
    $[10] = subqDisplayMode;
    $[11] = subqGroups;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  return t2;
};
type UseSubqGroupProps = {
  group: SubqSectionGroup;
  displayMode: QuestionQuery['subqDisplayMode'];
} & Pick<O.NonNullable<SubqLayout, 'question'>, 'question' | 'showForecasts'>;
const useSubqGroup = t0 => {
  const $ = _c(34);
  const {
    group: g,
    question
  } = t0;
  const question_id = question.id;
  const t1 = g.isRootQuestion ? null : g.id;
  const t2 = question.disableProposed ? approvedStatuses : undefined;
  let t3;
  if ($[0] !== question_id || $[1] !== t1 || $[2] !== t2) {
    t3 = {
      question_id,
      parent_id: t1,
      status: t2,
      pick: _temp8
    };
    $[0] = question_id;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const subqs = useSubqQuery(t3).data;
  let t4;
  if ($[4] !== subqs) {
    t4 = subqs ? subqs.filter(isApproved).length : undefined;
    $[4] = subqs;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const numberApproved = t4;
  let t5;
  if ($[6] !== g.id || $[7] !== g.isRootQuestion || $[8] !== question_id) {
    t5 = {
      question_id,
      subquestion_id: g.id,
      pick: _temp9,
      disabled: g.isRootQuestion
    };
    $[6] = g.id;
    $[7] = g.isRootQuestion;
    $[8] = question_id;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const subqCrud = useSubqQuery(t5);
  const t6 = !g.isRootQuestion;
  let t7;
  if ($[10] !== question_id || $[11] !== t6) {
    t7 = {
      question_id,
      pick: _temp10,
      disabled: t6
    };
    $[10] = question_id;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const questionCrud = useQuestionForm(t7);
  const t8 = g.isRootQuestion ? questionCrud.question?.cfg : subqCrud.data?.[0];
  let t9;
  if ($[13] !== g.cfg || $[14] !== t8) {
    t9 = {
      ...g.cfg,
      ...t8
    };
    $[13] = g.cfg;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const groupCfg = t9;
  let t10;
  if ($[16] !== g.id || $[17] !== g.isRootQuestion || $[18] !== questionCrud || $[19] !== subqCrud) {
    t10 = t11 => {
      const {
        delta
      } = t11;
      if (g.isRootQuestion) {
        questionCrud.crud.updateCfg({
          id: g.id,
          cfg_updates: delta
        });
      } else {
        subqCrud.updateCfg({
          id: g.id,
          cfg_updates: delta
        });
      }
    };
    $[16] = g.id;
    $[17] = g.isRootQuestion;
    $[18] = questionCrud;
    $[19] = subqCrud;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== groupCfg || $[22] !== t10) {
    t11 = {
      stored: groupCfg,
      onUpdate: t10
    };
    $[21] = groupCfg;
    $[22] = t10;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const groupForm = useDbForm(t11);
  let t12;
  if ($[24] !== g) {
    t12 = pick(g, ["id", "isRootQuestion"]);
    $[24] = g;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== groupCfg || $[27] !== groupForm || $[28] !== question_id || $[29] !== t12) {
    t13 = {
      question_id,
      ...t12,
      cfg: groupCfg,
      form: groupForm
    };
    $[26] = groupCfg;
    $[27] = groupForm;
    $[28] = question_id;
    $[29] = t12;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  const group = t13 as any as Group<true, Prettify<Pick<QuestionCfg, 'title' | 'subTitle' | 'description' | 'subquestionOrder' | 'forecastType' | 'resolutions'>>> | Group<false, Prettify<Pick<SubqCfg, 'title' | 'subTitle' | 'description' | 'subquestionOrder' | 'forecastType' | 'resolutions' | 'consequenceResolutions' | 'subquestionType'>>>;
  let t14;
  if ($[31] !== group || $[32] !== numberApproved) {
    t14 = {
      group,
      numberApproved
    };
    $[31] = group;
    $[32] = numberApproved;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  return t14;
};
export type SubqGroup = ReturnType<typeof useSubqGroup>['group'];
const SubqGroup = t0 => {
  const $ = _c(19);
  const {
    group: groupRaw,
    subqDisplayMode,
    showForecasts,
    question,
    singleGroup,
    disableProposed,
    children,
    gridMode
  } = t0;
  let t1;
  if ($[0] !== groupRaw || $[1] !== question || $[2] !== showForecasts || $[3] !== subqDisplayMode) {
    t1 = {
      group: groupRaw,
      displayMode: subqDisplayMode,
      showForecasts,
      question
    };
    $[0] = groupRaw;
    $[1] = question;
    $[2] = showForecasts;
    $[3] = subqDisplayMode;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const {
    group,
    numberApproved
  } = useSubqGroup(t1);
  const showGroup = group.isRootQuestion ? true : disableProposed ? (numberApproved ?? 0) > 0 : true;
  let t2;
  if ($[5] !== group || $[6] !== question.id || $[7] !== singleGroup) {
    t2 = {
      group,
      question_id: question.id,
      singleGroup
    };
    $[5] = group;
    $[6] = question.id;
    $[7] = singleGroup;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const listCommon = t2;
  let t3;
  if ($[9] !== children || $[10] !== disableProposed || $[11] !== gridMode || $[12] !== group || $[13] !== listCommon || $[14] !== numberApproved || $[15] !== showGroup || $[16] !== singleGroup || $[17] !== subqDisplayMode) {
    t3 = showGroup ? <WrapIf if={!singleGroup} wrap={c => <CardCollapsable sticky={true} classNames={{
      title: twMerge("py-[0.2em] text-lg "),
      container: "flex w-full flex-col items-stretch gap-4 mb-[2em]"
    }} title={singleGroup ? undefined : <>{group.isRootQuestion ? <MdHome className="icon-text" /> : <SubqTypeIcon type={group.cfg?.subquestionType} />}{" "}{group.cfg ? group.cfg?.title : "Sub-questions"}</>}>{c}</CardCollapsable>}>{children}<SubqList type="approved" disabled={subqDisplayMode == "order"} {...listCommon} gridMode={gridMode}>{subqDisplayMode == "order" && (numberApproved ?? 0) > 0 && <SubListOrder group={group} />}</SubqList>{subqDisplayMode == "forecast" && !disableProposed && <SubqList type="proposed" {...listCommon} />}</WrapIf> : undefined;
    $[9] = children;
    $[10] = disableProposed;
    $[11] = gridMode;
    $[12] = group;
    $[13] = listCommon;
    $[14] = numberApproved;
    $[15] = showGroup;
    $[16] = singleGroup;
    $[17] = subqDisplayMode;
    $[18] = t3;
  } else {
    t3 = $[18];
  }
  return t3;
};
const SubListOrder = t0 => {
  const $ = _c(6);
  const {
    group: g
  } = t0;
  const t1 = g.isRootQuestion ? null : g.id;
  let t2;
  if ($[0] !== g.question_id || $[1] !== t1) {
    t2 = {
      question_id: g.question_id,
      parent_id: t1,
      status: approvedStatuses,
      pick: _temp11
    };
    $[0] = g.question_id;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const subqsRaw = useSubqQuery(t2).data;
  let t3;
  if ($[3] !== g.form || $[4] !== subqsRaw) {
    const subqs = g.form.values && subqsRaw ? Subqs.sort(subqsRaw, {
      order: g.form.values.subquestionOrder,
      showForecasts: "all"
    }) : undefined;
    t3 = subqs && <MasonryGrid colWidth="20em" className="gap-4">{mapEntries(subqByType(subqs), (sqType, sqs) => <div key={sqType} className="space-y-1"><SubqTypeHeading type={sqType} /><OrderableList data={sqs} component={_temp12} onReorder={t4 => {
          const {
            ids
          } = t4;
          g.form.setFieldValue("subquestionOrder", [...(g.form.values?.subquestionOrder ?? []).filter(id_1 => !ids.includes(id_1)), ...ids]);
        }} /></div>)}</MasonryGrid>;
    $[3] = g.form;
    $[4] = subqsRaw;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
};
const SubqList = ({
  type,
  children,
  title,
  group,
  question_id,
  disabled,
  gridMode
}: {
  question_id: string;
  group: SubqGroup;
  type: 'approved' | 'proposed';
  title?: ReactNode;
  isSingleGroup?: boolean;
  disabled?: boolean;
  gridMode?: GridMode;
} & ChildrenProps) => {
  const {
    profile
  } = usePortal();
  const filters = useSubqFilters();
  const subqCrud = useSubqQuery({
    question_id,
    parent_id: group.isRootQuestion ? null : group.id,
    status: type == 'approved' ? approvedStatuses : ['proposed', 'draft'],
    showDraft: filters.query.showDraft,
    pick: ({
      id,
      cfg,
      created_at
    }) => ({
      id,
      cfg: pick(cfg, ['status', 'subquestionType', 'subquestionOrder']),
      created_at
    }),
    disabled: disabled
  });
  const subqsRaw = subqCrud.data;
  const uVotes = useQuestionVotesQuery({
    question_id,
    select: vs => vs.filter(v => v.subquestion_id && subqsRaw?.some(s => s.id == v.subquestion_id)),
    disabled: !subqsRaw || type == 'approved'
  });
  const order = group.form.values?.subquestionOrder;
  useEffect(() => {
    if (!order) return;
    subqSticky.reset();
  }, [order?.join('|')]);
  const subqSticky = useStickyOrder(subqsRaw && order != null ? match(type).with('approved', () => Subqs.sort(subqsRaw, {
    order,
    showForecasts: 'all'
  })).with('proposed', () => sortBy(subqsRaw.map(s_0 => ({
    ...s_0,
    vote: uVotes.data?.find(v_0 => v_0.subquestion_id == s_0.id)
  })), [s_1 => s_1.vote?.vote_total ?? 0, 'desc'], s_2 => s_2.created_at)).exhaustive() : undefined);
  const subqs = subqSticky.data;
  const create = useMutation({
    mutationFn: async (cfg_0: SubqCfg) => {
      await subqCrud.insert({
        created_by: profile.id,
        question_id,
        parent_id: group.isRootQuestion ? undefined : group.id,
        cfg: cfg_0
      }).then(r => {
        if (r.error) {
          showError(r.error, 'error proposing subquestion');
          return;
        }
      });
    }
  });
  return <div>
      {match(title).with(P.nullish, () => <></>).with(P.string, s_3 => <div className='flex justify-center items-center flex-wrap my-2 mr-2'>
            <h3 className={`uppercase mr-auto ${classes.text.subtle}`}>{s_3}</h3>
          </div>).otherwise(e => e)}
      {type == 'proposed' && (profile!.hasPermission('subquestion-edit') || subqSticky.showRefreshOrder) && <Sticky className='my-2'>
          {({
        isSticking
      }) => <HrText classNames={{
        hr: 'group-data-[sticking=true]:invisible',
        root: 'pt-1'
      }}>
              <div className='flex gap-2 items-center'>
                {profile!.hasPermission('subquestion-edit') && <NewSubqButton create={create} group={group} isSticking={isSticking} />}
                {subqSticky.showRefreshOrder && <Button leftSection={<HiRefresh />} onClick={() => subqSticky.reset()}>
                    Show with new order
                  </Button>}
              </div>
            </HrText>}
        </Sticky>}
      {children}
      {!disabled && doSafe(subqs, sqs => {
      const byType = subqByType(sqs);
      const singleType = keys(byType).length == 1;
      const sqProps = (sq: NotNull<typeof subqsRaw>[number]) => {
        const highlight = filters.query.subquestion == sq.id;
        return {
          question_id,
          subquestion_id: sq.id,
          group,
          listType: type,
          className: `rounded-lg  border-2 bg-white p-2 max-w-[60em] ${highlight ? 'border-teal-600/30' : 'border-gray-100'}`
        } satisfies SubqListItemProps;
      };
      return type == 'approved' ? <MasonryGrid colWidth={'40em'} className='gap-4'>
                {mapEntries(byType, (sqType, sqs_0) => {
          const gridChildren = sqs_0?.map(sq_0 => <SErrorBoundary key={sq_0.id}>
                      <SubqListItem key={sq_0.id} {...sqProps(sq_0)} />
                    </SErrorBoundary>);
          return <div key={sqType} className='space-y-2' style={{
            paddingTop: singleType ? '0.5em' : undefined
          }}>
                      {!singleType && <SubqTypeHeading type={sqType} />}
                      {sqType == 'consequence' ? <CardCollapsable classNames={{
              root: 'max-w-[60em]'
            }} title='Consequence Resolutions' variant='card' startCollapsed>
                          <ResolutionsEdit mode={group.isRootQuestion ? 'question-consequence-resolutions' : 'consequence-resolutions'} parentQ={{
                forecastType: group.cfg.forecastType,
                consequenceResolutions: group.isRootQuestion ? group.cfg.resolutions : group.cfg.consequenceResolutions
              }} qForm={group.form} />
                        </CardCollapsable> : undefined}
                      {gridMode == 'grid' ? <MasonryGrid colWidth='45em' className='gap-6'>
                          {gridChildren}
                        </MasonryGrid> : <div className='flex flex-col gap-2'>{gridChildren}</div>}
                    </div>;
        })}
              </MasonryGrid> : <MasonryGrid colWidth={'30em'} className='gap-4'>
                {sqs.map(sq_1 => <SErrorBoundary key={sq_1.id}>
                    <SubqListItem key={sq_1.id} {...sqProps(sq_1)} />
                  </SErrorBoundary>)}
              </MasonryGrid>;
    })}
    </div>;
};

// #endregion

// #region Item & Editing

const forecasterEditableStatus = ['draft', 'proposed'];
export const subqStatusItems = ({
  canEditQuestion,
  status
}: {
  canEditQuestion?: boolean;
  status?: SubqStatus;
}) => mapValues(subqStatusMd, (s, v) => ({
  ...s,
  disabled: canEditQuestion ? false : !forecasterEditableStatus.includes(v) || !forecasterEditableStatus.includes(status ?? '')
}));
export const SubqTitleEdit = t0 => {
  const $ = _c(16);
  const {
    form,
    onKeyDown
  } = t0;
  let t1;
  if ($[0] !== onKeyDown) {
    t1 = {
      onKeyDown
    };
    $[0] = onKeyDown;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const inputProps = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      input: "h3"
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== form) {
    t3 = form.getInputProps("title");
    $[3] = form;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== inputProps || $[6] !== t3) {
    t4 = <QuestionTitleInput {...inputProps} typeLabel="sub-question" classNames={t2} {...t3} autoFocus={true} />;
    $[5] = inputProps;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== form) {
    t5 = form.getInputProps("subTitle");
    $[8] = form;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== inputProps || $[11] !== t5) {
    t6 = <QuestionSubtitleInput {...inputProps} {...t5} />;
    $[10] = inputProps;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t4 || $[14] !== t6) {
    t7 = <>{t4}{t6}</>;
    $[13] = t4;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
};
export const onSubqTypeChange = ({
  value: value,
  form,
  resolutionForecasts,
  setValuesDescription
}: {
  value: SubqType;
  form: Pick<DbFormSubset<Pick<SubqCfg, 'subquestionType' | 'resolutions' | 'forecastType'>>, 'setValues' | 'values'>;
  resolutionForecasts?: ResolutionForecasts<ForecastFetched, UseForecastGroupApp>[];
  setValuesDescription?: string;
}) => {
  const values = form.values;
  if (!values || value == values.subquestionType) return;
  const resolutionDeps = {
    subquestionType: values.subquestionType,
    questionForecastType: values.forecastType,
    subquestionForecastType: values.forecastType
  } as ResolutionKeyDependents;

  // Keep existing resolutions that have forecasts
  const codesUsed = unique((resolutionForecasts ?? []).filter(r => !isEmpty(r.forecasts)).map(r => r.code));
  const resKeyOld = Resolution.key(resolutionDeps);
  const resKeyDepsNew = {
    ...resolutionDeps,
    subquestionType: value
  };
  const resKeyNew = Resolution.key(resKeyDepsNew);
  const resolutionsToKeep = filterObj(values.resolutions[resKeyOld] ?? {}, (k, v) => codesUsed.includes(k) && v.type == 'standard');
  const resolutionsNew = {
    ...R.defaultSubquestionResolutions(resKeyDepsNew),
    ...resolutionsToKeep
  };
  const cfgNew = {
    ...values,
    subquestionType: value,
    resolutions: {
      [resKeyOld]: {},
      [resKeyNew]: resolutionsNew
    }
  };

  // Validate before updating to avoid invalid states
  const testValid = zSubqCfg.safeParse(cfgNew);
  if (testValid.success) form.setValues({
    subquestionType: value,
    resolutions: cfgNew.resolutions
  }, setValuesDescription);
};
const zSubqListQuery = zQuestionQuery.pick({
  subq_editing: true,
  subquestion: true
});
type SubqListItemProps = {
  question_id: string;
  subquestion_id: string;
  group: SubqGroup;
  listType: 'approved' | 'proposed';
} & StyleProps;
const SubqListItem = ({
  question_id,
  subquestion_id,
  group,
  className,
  listType
}: SubqListItemProps) => {
  const {
    profile,
    readonly: portalReadonly,
    profilesUI
  } = usePortal();
  const question = pipe(useQuestionQuery({
    question_id,
    pick: r => ({
      id: r.id,
      ...pick(r.cfg, [...questionResultQuestionProps, 'tasked'])
    })
  }), q => q.data?.ok ? sansOk(q.data) as QuestionResultsQuestion & Pick<QuestionCfg, 'tasked'> : undefined);
  const subqCrud = useSubqQuery({
    question_id,
    subquestion_id,
    showDraft: true
  });
  const subq = subqCrud.data?.[0];
  const parentSubq = useSubqQuery({
    question_id,
    subquestion_id: group.id,
    pick: r_0 => ({
      id: r_0.id,
      ...r_0.cfg
    }),
    disabled: group.isRootQuestion
  }).data?.[0];
  const {
    getForecasts
  } = useForecasts({
    filter: {
      question_id,
      subquestion_id
    },
    disabled: listType == 'proposed'
  });
  const canEdit = subq ? profile?.hasPermission('question-edit-all') || profile?.hasPermission('subquestion-edit') && subq.created_by == profile.id : undefined;
  const readonly = !canEdit || portalReadonly;
  const resolutionForecasts = useMemo(() => {
    if (!question || !subq || !getForecasts || listType == 'proposed') return undefined;
    return F.getResolutionForecasts<ForecastFetched, UseForecastGroupApp>({
      getForecasts,
      question,
      subq: subq.cfg,
      sqId: subq.id,
      parentSubq
    });
  }, [question_id, subq, getForecasts, parentSubq]);
  const {
    isOwner
  } = useContext(QuestionContext)!;
  const groupCfg = group.isRootQuestion ? question : parentSubq;
  const cfg = subq && groupCfg ? {
    ...subq.cfg,
    defaultBounds: Bounds.withDefaults(subq.cfg.defaultBounds, groupCfg.defaultBounds).bounds
  } : undefined;
  const form = useDbForm({
    stored: cfg,
    onUpdate: ({
      delta
    }) => subqCrud.updateCfg({
      id: subquestion_id,
      cfg_updates: delta
    }),
    debounceSave: 500
  });
  const statusItems = useMemo(() => subqStatusItems({
    canEditQuestion: profile?.hasPermission('question-edit'),
    status: form.values?.status
  }), [profile, form.values?.status]);
  const qs = useQueryString(zSubqListQuery, {
    pick: r_1 => ({
      isEditing: r_1.subq_editing.includes(subquestion_id),
      subquestion: r_1.subquestion
    })
  });
  const editing = qs.query.isEditing;
  const setEditing = (v: boolean) => qs.setQuery(prev => ({
    subq_editing: v ? unique([...prev.subq_editing, subquestion_id]) : prev.subq_editing.filter(id => id != subquestion_id)
  }));
  const commentStats = useCommentStats({
    question_id,
    subquestion_id
  });
  const {
    setCommentExpand
  } = useCommentExpand();
  const forecastCount = resolutionForecasts?.flatMap(r_2 => r_2.forecasts).length;
  const highlight = qs.query.subquestion == subquestion_id;
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!highlight || !ref.current) return;
    delay(1).then(() => ref.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    }));
  }, [highlight, ref.current]);
  const id_0 = subquestion_id;
  const tour = useTourContext();
  const subqFilters = useSubqFilters();
  const [showForecastState] = useState<boolean>();
  const showForecasts = showForecastState ?? match(subqFilters.showForecasts).with('all', () => true).with('incomplete', () => false).with('none', () => false).exhaustive();
  const uVote = useVote({
    question_id,
    subquestion_id,
    disabled: listType == 'approved'
  });
  if (!cfg || !profile || !subq || !question || !form.values) return <></>;
  const values = form.values!;
  const {
    title,
    subTitle,
    subquestionType,
    forecastType,
    description
  } = values;
  const resolutionDeps = values?.resolutions && groupCfg ? {
    subquestionType: values.subquestionType,
    questionForecastType: groupCfg.forecastType,
    subquestionForecastType: values.forecastType
  } as ResolutionKeyDependents : undefined;
  const isTasked = question.tasked.includes(profile.id);
  const tasked = isTasked && listType == 'approved' ? {
    isTasked,
    progress: {
      completed: resolutionForecasts?.filter(r_3 => F.shouldShow(r_3.group.myForecast) && r_3.resolution.enableForecasts).length ?? 0,
      total: resolutionForecasts?.filter(r_4 => r_4.resolution.enableForecasts).length ?? 0
    }
  } : undefined;
  const dupeMute = useMutation({
    mutationFn: async ({
      copy_forecasts
    }: {
      copy_forecasts: boolean;
    }) => dupeQuestion({
      type: 'subquestion',
      copy_forecasts,
      profile,
      id: id_0
    }).then(r_5 => {
      if (!r_5.ok) throw new Error(r_5.message);
      return r_5.value;
    }),
    onSuccess: subquestion => {
      delay(100).then(() => qs.setQuery({
        subquestion
      }));
    },
    onError: error => {
      showError(error, 'Error duplicating sub-question');
    }
  });
  return <div ref={ref} id={`subq-${id_0}`} onClick={e => e.stopPropagation()} className={`flex flex-col gap-2  group ${className ?? ''}`}>
      <div className='flex items-start'>
        <div className='flex-grow'>
          {editing ? <SubqTitleEdit form={form} /> : <QuestionTitle type='subq' {...{
          title,
          subTitle,
          description
        }} heightMode={showForecasts ? 'long' : 'short'} />}
        </div>
        <EditingButton className={twMerge(editing ? '' : 'group-hover:opacity-100 opacity-0')} editing={editing ?? false} setEditing={setEditing} isDirty={form.isDirty} style={tour.tip?.id == 'subq-edit-button' ? {
        opacity: '100'
      } : {}} {...!editing && !readonly ? tourTipProps('subq-edit-button') : {}} />
        {!readonly && <OverflowMenu className='ml-auto group-hover:opacity-100 opacity-0' width='max-content'>
            <Menu.Item leftSection={<HiTrash />} onClick={() => {
          openConfirmModal({
            title: `Delete sub-question?`,
            centered: true,
            children: <span>
                      Are you sure you want to delete <b>{values.title}</b>?
                    </span>,
            labels: {
              confirm: `Delete`,
              cancel: 'Cancel'
            },
            confirmProps: {
              color: 'red',
              variant: 'filled'
            },
            onConfirm: () => {
              subqCrud.update({
                id: id_0,
                deleted: true,
                updated_at: DateTime.utc().toISO()
              });
            }
          });
        }}>
              Delete
            </Menu.Item>
            <Menu.Item leftSection={dupeMute.isPending ? <Loader size='sm' /> : <HiClipboardCopy />} disabled={dupeMute.isPending} onClick={() => {
          dupeMute.mutate({
            copy_forecasts: false
          });
        }}>
              Duplicate
            </Menu.Item>
            <Menu.Item leftSection={dupeMute.isPending ? <Loader size='sm' /> : <HiClipboardCopy />} disabled={dupeMute.isPending} onClick={() => {
          dupeMute.mutate({
            copy_forecasts: true
          });
        }}>
              Duplicate with forecasts & comments
            </Menu.Item>
          </OverflowMenu>}
      </div>
      <div className='flex items-center gap-2 self-start'>
        {editing && resolutionDeps ? <SubqTypeCombo value={subquestionType} onChange={value => onSubqTypeChange({
        value,
        form,
        resolutionForecasts
      })} existingForecasts={forecastCount} withinPortal /> : <SubqTypeIcon variant='icon' type={subquestionType} size='18px' />}
        <ForecastTypeCombo value={forecastType} onChange={t => form.setFieldValue('forecastType', t)} existingForecasts={forecastCount} readonly={!editing} withinPortal />
        <QuestionTileItems {...{
        profile: listType == 'approved' ? undefined : doSafe(subq?.created_by, c => profilesUI?.[c])
      }} />
        <CommentStatuses statusSummary={commentStats.statusSummary} />
        {(editing || (listType == 'approved' ? values.status != listType : true)) && <QuestionStatusButton mds={statusItems} value={values.status} onChange={s => {
        form.setFieldValue('status', s);
        if (tour.tip?.id == 'subq-status-editable') tour.next('ok');
      }} readonly={readonly || Object.values(statusItems).every(s_0 => s_0.disabled)} {...tourTipProps('subq-status-editable', isOwner)} />}
        <ActionIcon styles={{
        root: {
          overflow: 'visible'
        }
      }} onClick={() => {
        qs.setQuery({
          subquestion: id_0
        });
        setCommentExpand(true);
      }}>
          <CommentsStats unread={commentStats.unread} total={commentStats.total} />
        </ActionIcon>
        {listType == 'proposed' && <VoteButtons {...reactionPropsForVote({
        vote: uVote.vote,
        save: uVote.save,
        profile,
        subq
      })} />}
        {tasked && <TaskedStatus {...tasked} />}
      </div>

      {editing && <>
          <QuestionCommonForm readonly={false} form={form} topItems={<Tooltip label='Enable/disable sub-questions for this, forecasting and question proposals are displayed in a new section bellow'>
                <div>
                  <Switch label='allow conditionals' {...form.getInputProps('childrenEnabled', {
            type: 'checkbox'
          })} />
                </div>
              </Tooltip>} isOwner={isOwner} existingForecasts={forecastCount} />
          {resolutionDeps && groupCfg && <ResolutionsEdit mode={'subquestion'} className='mt-6' qForm={form} parentQ={{
        forecastType: groupCfg.forecastType,
        consequenceResolutions: group.isRootQuestion ? groupCfg.resolutions as ResolutionsStandalone : subq.cfg.consequenceResolutions
      }} />}
        </>}
      {notNullOrEmpty(resolutionForecasts) && <SubqForecasts question_id={question_id} subquestion_id={id_0} resolutionForecasts={resolutionForecasts} displayMode={subqFilters.query.subqDisplayMode} className='' subQForm={form} showForecasts={showForecasts} editing={editing} />}
    </div>;
};
export const reactionPropsForVote = ({
  profile,
  vote,
  save,
  subq
}: {
  profile?: ProfileUI;
  vote: ReactionSummaryOfType<'vote'> | undefined;
  save?: (cfg: Pick<ReactionCfg<'vote'>, 'vote'>) => void;
  subq: Pick<SubqFetched, 'id' | 'created_by'> & {
    cfg: Pick<SubqCfg, 'status'>;
  };
}): VoteButtonsProps => {
  if (profile == null) return {};
  return {
    value: vote?.my_reaction_cfg?.vote ?? 0,
    total: vote?.vote_total,
    disabledReason: profile.id == subq.created_by ? `You can't vote for your own question` : !['proposed', 'approved', 'paused'].includes(subq.cfg.status) ? `Can only vote for ${questionTypeMd.subquestion.label.toLowerCase()}s when they are propose/approved/paused` : undefined,
    onChange: v => save?.({
      vote: v
    })
  } satisfies VoteButtonsProps;
};
export const ForecastTypeCombo = t0 => {
  const $ = _c(25);
  let existingForecasts;
  let onChange;
  let onDropdownOpen;
  let props;
  let readonly;
  let showLabel;
  let value;
  let withinPortal;
  if ($[0] !== t0) {
    ({
      value,
      onChange,
      existingForecasts,
      readonly,
      onDropdownOpen,
      withinPortal,
      showLabel,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = existingForecasts;
    $[2] = onChange;
    $[3] = onDropdownOpen;
    $[4] = props;
    $[5] = readonly;
    $[6] = showLabel;
    $[7] = value;
    $[8] = withinPortal;
  } else {
    existingForecasts = $[1];
    onChange = $[2];
    onDropdownOpen = $[3];
    props = $[4];
    readonly = $[5];
    showLabel = $[6];
    value = $[7];
    withinPortal = $[8];
  }
  let t1;
  if ($[9] !== onDropdownOpen) {
    t1 = {
      onDropdownOpen
    };
    $[9] = onDropdownOpen;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  const store = useCombobox(t1);
  const t2 = !readonly;
  let t3;
  if ($[11] !== existingForecasts || $[12] !== onChange || $[13] !== props || $[14] !== store || $[15] !== value || $[16] !== withinPortal) {
    t3 = c => <Combo store={store} withinPortal={withinPortal} position="bottom-start" onOptionSubmit={s => {
      store.closeDropdown();
      onChange?.(s as QuestionForecastType);
    }}><Combo.Target><div className="flex items-center cursor-pointer gap-1" onClick={() => store.openDropdown()} {...props}>{c}<HiSelector /></div></Combo.Target><Combo.Dropdown>{existingForecasts != null && existingForecasts > 0 && <Combo.Header><Alert variant="light" color="orange" icon={<HiExclamationCircle size={18} />}>There are forecasts that will become redundant if you change the type</Alert></Combo.Header>}<Combo.Options>{keys(questionForecastMd).map(t => {
            const {
              desc,
              label
            } = questionForecastMd[t];
            return <Combo.Option key={t} value={t} className="!py-2 space-y-1"><div className="flex items-center gap-2 ">{t == value && <CheckIcon size="1em" color="gray" />}<ForecastTypeIcon type={t} /><div className="font-semibold">{upperFirst(label)}</div></div><div className="text-gray-600">{desc}</div></Combo.Option>;
          })}</Combo.Options></Combo.Dropdown></Combo>;
    $[11] = existingForecasts;
    $[12] = onChange;
    $[13] = props;
    $[14] = store;
    $[15] = value;
    $[16] = withinPortal;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  let t4;
  if ($[18] !== showLabel || $[19] !== value) {
    t4 = value && <div className="flex items-center gap-2">{value && <ForecastTypeIcon type={value} />}{showLabel && <div className="font-semibold">{upperFirst(questionForecastMd[value]?.label ?? "")}</div>}</div>;
    $[18] = showLabel;
    $[19] = value;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  let t5;
  if ($[21] !== t2 || $[22] !== t3 || $[23] !== t4) {
    t5 = <WrapIf if={t2} wrap={t3}>{t4}</WrapIf>;
    $[21] = t2;
    $[22] = t3;
    $[23] = t4;
    $[24] = t5;
  } else {
    t5 = $[24];
  }
  return t5;
};
export const ForecastTypeIcon = t0 => {
  const $ = _c(16);
  const {
    type
  } = t0;
  const {
    label,
    desc
  } = questionForecastMd[type];
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      size: "18px",
      className: "text-gray-400"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== label) {
    t2 = upperFirst(label);
    $[1] = label;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <b>{t2}</b>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <br />;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== desc || $[7] !== t3) {
    t5 = <div>{t3}{t4}{desc}</div>;
    $[6] = desc;
    $[7] = t3;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== type) {
    t6 = match(type).with("likelihood", _temp13).with("amount", _temp14).exhaustive();
    $[9] = type;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t6) {
    t7 = <div>{t6}</div>;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t5 || $[14] !== t7) {
    t8 = <IconContext.Provider value={t1}><Tooltip withinPortal={true} label={t5}>{t7}</Tooltip></IconContext.Provider>;
    $[13] = t5;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  return t8;
};
export const SubqTypeCombo = t0 => {
  const $ = _c(46);
  let existingForecasts;
  let onChange;
  let onDropdownOpen;
  let props;
  let showLabel;
  let value;
  let withinPortal;
  if ($[0] !== t0) {
    ({
      value,
      onChange,
      existingForecasts,
      showLabel,
      onDropdownOpen,
      withinPortal,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = existingForecasts;
    $[2] = onChange;
    $[3] = onDropdownOpen;
    $[4] = props;
    $[5] = showLabel;
    $[6] = value;
    $[7] = withinPortal;
  } else {
    existingForecasts = $[1];
    onChange = $[2];
    onDropdownOpen = $[3];
    props = $[4];
    showLabel = $[5];
    value = $[6];
    withinPortal = $[7];
  }
  let t1;
  if ($[8] !== onDropdownOpen) {
    t1 = {
      onDropdownOpen
    };
    $[8] = onDropdownOpen;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  const store = useCombobox(t1);
  let t2;
  let t3;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      flip: true,
      shift: true
    };
    t3 = {
      dropdown: {
        maxWidth: "min(30em, 100vw)",
        paddingTop: "0.5em"
      }
    };
    $[10] = t2;
    $[11] = t3;
  } else {
    t2 = $[10];
    t3 = $[11];
  }
  let t4;
  if ($[12] !== onChange || $[13] !== store) {
    t4 = s => {
      store.closeDropdown();
      onChange(s as SubqType);
    };
    $[12] = onChange;
    $[13] = store;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== store) {
    t5 = () => store.openDropdown();
    $[15] = store;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== value) {
    t6 = <SubqTypeIcon type={value} variant="icon" />;
    $[17] = value;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== showLabel || $[20] !== value) {
    t7 = showLabel && <div className="font-semibold">{upperFirst(value ?? "")}</div>;
    $[19] = showLabel;
    $[20] = value;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <HiSelector />;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== props || $[24] !== t5 || $[25] !== t6 || $[26] !== t7) {
    t9 = <Combo.Target><div {...props} className="flex items-center cursor-pointer gap-1" onClick={t5}>{t6}{t7}{t8}</div></Combo.Target>;
    $[23] = props;
    $[24] = t5;
    $[25] = t6;
    $[26] = t7;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== existingForecasts) {
    t10 = existingForecasts != null && existingForecasts > 0 && <Alert variant="light" color="orange" icon={<HiExclamationCircle size={18} />}>There are forecasts that will become redundant if you change the type</Alert>;
    $[28] = existingForecasts;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <TourInfo className="py-2" tips="new-question-propose-subq-type" />;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== t10) {
    t12 = <Combo.Header className="flex justify-end items-center gap-2 pb-4">{t10}{t11}</Combo.Header>;
    $[31] = t10;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== value) {
    t13 = keys(subqMd).map(t => {
      const {
        desc
      } = subqMd[t];
      return <Combo.Option key={t} value={t} className="!py-2 space-y-1"><div className="flex items-center gap-2 ">{t == value && <CheckIcon size="1em" color="gray" />}<SubqTypeIcon type={t} variant="icon" /><div className="font-semibold">{upperFirst(t)}</div></div><div className="text-gray-600">{desc}</div></Combo.Option>;
    });
    $[33] = value;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== t13) {
    t14 = <Combo.Options>{t13}</Combo.Options>;
    $[35] = t13;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] !== t12 || $[38] !== t14) {
    t15 = <Combo.Dropdown>{t12}{t14}</Combo.Dropdown>;
    $[37] = t12;
    $[38] = t14;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] !== store || $[41] !== t15 || $[42] !== t4 || $[43] !== t9 || $[44] !== withinPortal) {
    t16 = <Combo withinPortal={withinPortal} store={store} keepMounted={true} position="top" middlewares={t2} styles={t3} onOptionSubmit={t4}>{t9}{t15}</Combo>;
    $[40] = store;
    $[41] = t15;
    $[42] = t4;
    $[43] = t9;
    $[44] = withinPortal;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  return t16;
};
export const SubqTypeHeading = t0 => {
  const $ = _c(9);
  const {
    type,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = twMerge("flex items-center gap-2 bg-gray-50 pl-2 pr-3 py-1 rounded-lg ", "data-[sticking=true]:w-fit data-[sticking=true]:shadow-md data-[sticking=true]:bg-white", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  let t3;
  if ($[2] !== type) {
    t2 = <div><SubqTypeIcon variant="icon" type={type} size="1.3rem" /></div>;
    t3 = <div><LabelType className="text-sm">{type}</LabelType></div>;
    $[2] = type;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t1 || $[6] !== t2 || $[7] !== t3) {
    t4 = <Sticky className={t1}>{t2}{t3}</Sticky>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
};
export const SubqTypeIcon = t0 => {
  const $ = _c(5);
  const {
    type,
    size,
    variant: t1,
    className
  } = t0;
  const variant = t1 === undefined ? "text" : t1;
  let t2;
  if ($[0] !== className || $[1] !== size || $[2] !== type || $[3] !== variant) {
    t2 = type ? <IconContext.Provider value={{
      size: size ?? (variant == "text" ? "1.2em" : "18px")
    }}><Tooltip withinPortal={true} label={<div><b>{upperFirst(type)}</b><br />{subqMd[type].desc}</div>}><div className={twMerge(variant == "text" ? "inline-block mr-1.5 top-[0.3em] relative" : "", className)}>{match(type).with("cause", _temp15).with("consequence", _temp16).with("related", _temp17).exhaustive()}</div></Tooltip></IconContext.Provider> : undefined;
    $[0] = className;
    $[1] = size;
    $[2] = type;
    $[3] = variant;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};

// #endregion
function _temp(r) {
  return omit(r, ["subq_resolution_closed", "subq_resolution_open"]);
}
function _temp5() {
  return <HiViewGrid />;
}
function _temp6() {
  return <HiViewList />;
}
function _temp7(item) {
  return match(item.value).with("grid", _temp5).with("list", _temp6).exhaustive();
}
function _temp8(t0) {
  const {
    cfg
  } = t0;
  return pick(cfg, ["status"]);
}
function _temp9(r) {
  return pick(r.cfg, ["subquestionOrder", "forecastType", "resolutions", "subquestionType", "title"]);
}
function _temp10(r_0) {
  return pick(r_0, ["subquestionOrder", "forecastType", "resolutions"]);
}
function _temp11(t0) {
  const {
    id,
    cfg,
    created_at
  } = t0;
  return {
    id,
    cfg: pick(cfg, ["status", "subquestionType", "childrenEnabled", "subquestionOrder", "title", "subTitle"]),
    created_at
  };
}
function _temp12(t0) {
  const {
    id: id_0,
    cfg: t1
  } = t0;
  const {
    title,
    subTitle
  } = t1;
  return <div className="p-2" key={id_0}><QuestionTitle type="subq" {...{
      title,
      subTitle
    }} /></div>;
}
function _temp13() {
  return <IconLikelihood />;
}
function _temp14() {
  return <IconAmount />;
}
function _temp15() {
  return <IconCause className="text-green-600/60" />;
}
function _temp16() {
  return <IconConsequence className="text-amber-600/60" />;
}
function _temp17() {
  return <IconRelated className="text-blue-600/60" />;
}